import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../Common/Layout";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { formatPhoneNumberDash } from "../utils/dateFormat";

interface CompareQuotesProps {
  onNext?: (data?: any) => void;
  onPrevious?: () => void;
  workflowData?: any;
  currentStep?: number;
  totalSteps?: number;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  insuredId?: string;
  selectedState?: string;
}

interface AncillaryQuoteCard {
  id: string;
  policyNumber?: string;
  effectiveDate?: string;
  totalPremium: number;
}

interface InsuranceQuote {
  id: string;
  carrier: string;
  logo: string;
  totalPremium: number;
  downPayment: number;
  payments: number | string;
  installmentPayment: number;
  totalDueToday: number;
  agencyFee: number;
  transpayServiceFee: number;
  // policyFee: number;
  wayRTG6MonPrice: number;
  wayRTG3MonPrice: number;
  wayRoadside6MonPrice: number;
  wayRoadside3MonPrice: number;
  hospitalIndIndividualPrice: number;
  hospitalIndFamilyPrice: number;
  telemedicinePrice: number;
  discountPackagePrice: number;
  identityTheftIndPrice: number;
  status?: string;
  errors?: string[];
  warnings?: string[];
  bridgeUrl?: string;
}

interface CompareQuotesProps {
  workflowData?: any;
  setWorkflowData?: (updater: (prev: any) => any) => void;
  [key: string]: any;
  loadingAncillaryCards?: boolean;
}

export default function CompareQuotes(props: CompareQuotesProps = {}) {
  const { onNext, onPrevious, workflowData, currentStep, totalSteps, isFirstStep, isLastStep, insuredId, loadingAncillaryCards, selectedState } =
    props;
  const navigate = useNavigate();

  const [showTranspayModal, setShowTranspayModal] = useState(false);
  const [transpayCarrier, setTranspayCarrier] = useState<string | null>(null);
  const [bridgeUrl, setBridgeUrl] = useState<string | null>(null);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);

  const Content = () => {
    // For ancillary DataTable rows expand/collapse
    const [showAllAncillaryRows, setShowAllAncillaryRows] = useState(false);
    const toast = React.useRef<Toast>(null);
    // Type for DataTable row object
    type TableRow = {
      feature: string | React.ReactNode;
      checkbox: React.ReactNode | null;
      [key: string]: React.ReactNode | string | null;
    };

    // Loading state — true until quotes array is available from workflowData
    // In ancillary flow there are no quotes, so start as false immediately
    const _isAncFlow = workflowData?.step1?.ancillaryFlow === true || localStorage.getItem("ancillaryFlow") === "true";
    const [quotesLoading, setQuotesLoading] = useState<boolean>(!_isAncFlow && !Array.isArray(workflowData?.step6?.quotes));

    useEffect(() => {
      const isAncFlow = workflowData?.step1?.ancillaryFlow === true || localStorage.getItem("ancillaryFlow") === "true";
      if (isAncFlow || Array.isArray(workflowData?.step6?.quotes)) {
        setQuotesLoading(false);
      }
    }, [workflowData?.step6?.quotes]);

    // Config state from API
    const [config, setConfig] = useState<any | null>(null);
    // Ancillary checkboxes state, initialized from config
    const [globalAncillaries, setGlobalAncillaries] = useState({
      wayRTG6Mon: false,
      wayRTG3Mon: false,
      wayRoadside6Mon: false,
      wayRoadside3Mon: false,
      hospitalIndIndividual: false,
      hospitalIndFamily: false,
      telemedicine: false,
      discountPackage: false,
      identityTheftInd: false,
    });
    // Agency/tranzpay fees from config
    const [agencyFee, setAgencyFee] = useState(30);
    const [tranzpayServiceFee, setTranzpayServiceFee] = useState(15);

    React.useEffect(() => {
      const layoutContent = document.querySelector(".layout-content");
      if (layoutContent) {
        layoutContent.scrollTo({ top: 0, behavior: "auto" });
      }
      window.scrollTo({ top: 0, behavior: "auto" });
      setGlobalAncillaries(workflowData?.step7?.globalAncillaries || {});
      setAgencyFee(workflowData?.step7?.agencyFee || 0);
      setTranzpayServiceFee(workflowData?.step7?.tranzpayServiceFee ?? 15);
      if (workflowData?.step7?.selectedQuotes || workflowData?.step7?.globalAncillaries) {
        setIsPaymentDone(true);
      }
    }, []);

    // Fetch config on mount or when selectedState changes
    useEffect(() => {
      if (!selectedState) return;
      // Skip API call if user navigated back — restore saved state instead
      const hasSavedState = !!(
        workflowData?.step7?.selectedQuotes ||
        workflowData?.step7?.selectedData?.selectedQuotes ||
        workflowData?.step7?.globalAncillaries ||
        workflowData?.step7?.selectedData?.globalAncillaries
      );
      if (hasSavedState) return;
      axios({
        method: "get",
        url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/compareinsurancequoteconfiguration?state=${selectedState}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          "Content-Type": "Application/json",
        },
      })
        .then((response: any) => {
          setConfig(response.data);
          // Set sortBy from config
          if (response.data.sortBy) {
            const apiSortBy = response.data.sortBy.trim();
            let sortKey = "price";
            if (apiSortBy === "Instalment Payment" || apiSortBy === "Installment Payment") sortKey = "price";
            else if (apiSortBy === "Total Premium") sortKey = "premium";
            else if (apiSortBy === "Total Due Today (All Fees & Ancillaries)") sortKey = "totalDueToday";
            else if (apiSortBy === "Total Auto Premium") sortKey = "totalAutoPremium";
            setSortBy(sortKey);
          }
          // Set ancillaries from config
          setGlobalAncillaries({
            wayRTG6Mon: response.data.wayPlusRTG6Month === "Yes",
            wayRTG3Mon: response.data.wayPlusRTG3Month === "Yes",
            wayRoadside6Mon: response.data.wayPlusRoadside6Month === "Yes",
            wayRoadside3Mon: response.data.wayPlusRoadside3Month === "Yes",
            hospitalIndIndividual: response.data.hospitalIndIndividual === "Yes",
            hospitalIndFamily: response.data.hospitalIndFamily === "Yes",
            telemedicine: response.data.telemedicine === "Yes",
            discountPackage: response.data.discountPackage === "Yes",
            identityTheftInd: response.data.identityTheftInd === "Yes",
          });
          // Set fees
          setAgencyFee(response.data.agencyFee ?? 30);
          setTranzpayServiceFee(response.data.tranzpayServiceFee ?? 15);
        })
        .catch(() => {});
    }, [selectedState]);

    // Mapping for Way ancillary TemplateQuoteIds
    const wayMappings = {
      wayRTG3Mon: "05ed8e41-c814-4cd4-933c-30f889fb2d33",
      wayRTG6Mon: "16798d5d-8933-473a-9478-b14265c00228",
      wayRoadside3Mon: "95e2f42c-3da3-45ed-bfe1-3baa73cf9a2d",
      wayRoadside6Mon: "b79b210b-e125-4b23-a840-3557e9d451b9",
    };

    // Type for ancillary keys
    type AncillaryKey = keyof typeof globalAncillaries;
    const [showSendModal, setShowSendModal] = useState(false);
    const [sendMethod, setSendMethod] = useState<"email" | "text" | null>(null);
    const [senderEmail, setSenderEmail] = useState("");
    const [receiverEmail, setReceiverEmail] = useState("");
    const [emailBody, setEmailBody] = useState("");
    const [recipient, setRecipient] = useState("");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [selectedEmailQuote, setSelectedEmailQuote] = useState<InsuranceQuote | null>(null);

    const [sendStatus, setSendStatus] = useState<string | null>(null);
    // For single quote selection
    const [selectedQuotes, setSelectedQuotes] = useState<string | null>(() => {
      const sq = workflowData?.step7?.selectedQuotes;
      if (Array.isArray(sq)) return sq[0] || null;
      return sq || null;
    });
    // Use string type to allow dynamic assignment from API and dropdown
    const [sortBy, setSortBy] = useState<string>("price");
    // Notes state
    const [notes, setNotes] = useState<{ text: string; date: string; author: string; id: string }[]>([]);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showNotesDrawer, setShowNotesDrawer] = useState(false);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [noteValue, setNoteValue] = useState("");
    const [editNoteIdx, setEditNoteIdx] = useState<number | null>(null);
    const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
    const [ancillaryQuoteCards, setAncillaryQuoteCards] = useState<AncillaryQuoteCard[]>([]);
    const lastFetchedInsuredId = React.useRef<string | null>(null);
    // const [loadingAncillaryCards, setLoadingAncillaryCards] = useState(false);
    const loadingAncillaryRef = React.useRef(false);
    const fetchedIds = React.useRef(new Set<string>());
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingWayTemplates, setPendingWayTemplates] = useState<string[]>([]);
    const [pendingSelectedQuoteId, setPendingSelectedQuoteId] = useState<string | null>(null);
    const [proceedBtnLabel, setProceedBtnLabel] = useState("Continue");
    const [vehicleCountFromApi, setVehicleCountFromApi] = useState<number>(1);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [selectedQuoteErrors, setSelectedQuoteErrors] = useState<{ carrier: string; errors: string[]; warnings: string[] } | null>(null);
    const [expandedCarriers, setExpandedCarriers] = useState<Set<string>>(new Set());

    // Memoize the insuredId to prevent unnecessary re-fetches
    const insuredIdForFetch = React.useMemo(
      () => workflowData?.step1?.insuredId || localStorage.getItem("currentInsuredId"),
      [workflowData?.step1?.insuredId],
    );

    React.useEffect(() => {}, [workflowData]);

    // Helper function to get carrier logo based on carrier name
    const getCarrierLogo = (carrierName: string): string => {
      const name = carrierName?.toLowerCase() || "";
      if (name.includes("state farm")) return "🏠";
      if (name.includes("progressive")) return "📈";
      if (name.includes("allstate")) return "🤝";
      if (name.includes("geico")) return "🦎";
      if (name.includes("liberty")) return "🗽";
      if (name.includes("safeco")) return "🛡️";
      if (name.includes("nationwide")) return "🏢";
      if (name.includes("farmers")) return "🚜";
      if (name.includes("usaa")) return "⭐";
      if (name.includes("traveler")) return "☂️";
      return "🚗"; // Default logo for unknown carriers
    };

    // Group quotes by carrier name
    const quotesGroupedByCarrier = useMemo(() => {
      if (!workflowData?.step6?.quotes || !Array.isArray(workflowData.step6.quotes)) {
        return {};
      }

      const grouped: Record<string, any[]> = {};

      workflowData.step6.quotes.forEach((quote: any) => {
        const carrier = quote.carrierName || "Unknown Carrier";
        if (!grouped[carrier]) {
          grouped[carrier] = [];
        }
        grouped[carrier].push(quote);
      });

      return grouped;
    }, [workflowData?.step6?.quotes]);

    // Carrier color mapping for visual differentiation
    const carrierColors = React.useMemo(() => {
      const colors = [
        "#e0f2fe", // light blue
        "#f0fdf4", // light green
        "#fef3c7", // light yellow
        "#fce7f3", // light pink
        "#e0e7ff", // light indigo
        "#f3e8ff", // light purple
        "#fed7aa", // light orange
        "#dbeafe", // sky blue
      ];
      const carriers = Object.keys(quotesGroupedByCarrier);
      const colorMap: Record<string, string> = {};
      carriers.forEach((carrier, idx) => {
        colorMap[carrier] = colors[idx % colors.length];
      });
      return colorMap;
    }, [quotesGroupedByCarrier]);

    // Get visible quotes based on expansion state
    const visibleQuotes = useMemo(() => {
      if (!workflowData?.step6?.quotes || !Array.isArray(workflowData.step6.quotes)) {
        return [];
      }

      const result: any[] = [];
      const processedCarriers = new Set<string>();

      // Iterate through original quotes to maintain order
      workflowData.step6.quotes.forEach((quote: any) => {
        const carrier = quote.carrierName || "Unknown Carrier";

        if (processedCarriers.has(carrier)) {
          // Skip if we've already processed this carrier
          return;
        }

        processedCarriers.add(carrier);
        const carrierQuotes = quotesGroupedByCarrier[carrier] || [];

        if (expandedCarriers.has(carrier)) {
          // Show all quotes for expanded carriers in their original order
          result.push(...carrierQuotes);
        } else {
          // Show only the quote with lowest down payment
          const lowestQuote = carrierQuotes.reduce((min, quote) => {
            const minDown = min.premiumAndPaymentDetails?.downPaymentAmount || 0;
            const currentDown = quote.premiumAndPaymentDetails?.downPaymentAmount || 0;
            return currentDown < minDown ? quote : min;
          }, carrierQuotes[0]);
          result.push(lowestQuote);
        }
      });

      return result;
    }, [quotesGroupedByCarrier, expandedCarriers, workflowData?.step6?.quotes]);

    // Map API response data to InsuranceQuote interface
    const quotes: InsuranceQuote[] = useMemo(() => {
      return visibleQuotes.map((apiQuote: any) => {
        const totalPremium = apiQuote.premiumAndPaymentDetails?.totalPremium || 0;
        const downPayment = apiQuote.premiumAndPaymentDetails?.downPaymentAmount || 0;
        const installmentAmount = apiQuote.premiumAndPaymentDetails?.installmentAmount || 0;
        const numberOfPayments = apiQuote.premiumAndPaymentDetails?.numberOfPayments || 0;

        return {
          id: apiQuote.quoteIds || apiQuote.bridgeUrl || apiQuote.carrierName || crypto.randomUUID(),
          carrier: apiQuote.carrierName || "Unknown Carrier",
          logo: getCarrierLogo(apiQuote.carrierName),
          totalPremium: totalPremium,
          downPayment: downPayment,
          payments: numberOfPayments,
          installmentPayment: installmentAmount,
          totalDueToday: downPayment, // Base value, will be recalculated with ancillaries
          agencyFee: agencyFee,
          transpayServiceFee: tranzpayServiceFee,
          wayRTG6MonPrice: 0,
          wayRTG3MonPrice: 0,
          wayRoadside6MonPrice: 0,
          wayRoadside3MonPrice: 0,
          hospitalIndIndividualPrice: 0,
          hospitalIndFamilyPrice: 0,
          telemedicinePrice: 0,
          discountPackagePrice: 0,
          identityTheftIndPrice: 0,
          status: apiQuote.status,
          errors: apiQuote.errors || [],
          warnings: apiQuote.warnings || [],
          bridgeUrl: apiQuote.bridgeUrl || "",
        };
      });
    }, [visibleQuotes, agencyFee, tranzpayServiceFee]);

    // Toggle carrier expansion
    const toggleCarrierExpansion = (carrier: string) => {
      setExpandedCarriers((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(carrier)) {
          newSet.delete(carrier);
        } else {
          newSet.add(carrier);
        }
        return newSet;
      });
    };

    // Check if carrier has multiple quotes
    const hasMultipleQuotes = (carrier: string) => {
      return (quotesGroupedByCarrier[carrier]?.length || 0) > 1;
    };
    const isAncillaryFlow = React.useMemo(() => {
      return workflowData?.step1?.ancillaryFlow === true || localStorage.getItem("ancillaryFlow") === "true";
    }, [workflowData]);

    useEffect(() => {
      if (isAncillaryFlow) {
        setAncillaryQuoteCards(workflowData?.step6?.ancillaryCards || []);
      }
    }, [workflowData, isAncillaryFlow]);

    // useEffect(() => {
    //   if (isAncillaryFlow && insuredIdForFetch && !fetchedIds.current.has(insuredIdForFetch)) {
    //     fetchedIds.current.add(insuredIdForFetch);
    //     lastFetchedInsuredId.current = insuredIdForFetch;
    //     setAncillaryQuoteCards([]); // Clear previous cards
    //     setLoadingAncillaryCards(true);
    //     axios
    //       .get(`${process.env.REACT_APP_ACE_API_Url}/api/ACE/policies?insuredId=${insuredIdForFetch}&ncToken=${localStorage.getItem("NCToken")}`, {
    //         headers: {
    //           Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
    //         },
    //       })
    //       .then((response: any) => {
    //         const policies = response.data.value || [];
    //         const cards = policies.map((policy: any) => ({
    //           id: policy.databaseId,
    //           policyNumber: policy.number,
    //           effectiveDate: policy.effectiveDate,
    //           totalPremium: policy.totalPremium,
    //         }));
    //         setAncillaryQuoteCards(cards);
    //       })
    //       .catch((error: any) => {})
    //       .finally(() => {
    //         setLoadingAncillaryCards(false);
    //       });
    //   }
    // }, [isAncillaryFlow, insuredIdForFetch]);

    React.useEffect(() => {
      if (isAncillaryFlow && selectedQuotes) {
        getVehiclesCountById(selectedQuotes);
      }
    }, [isAncillaryFlow, selectedQuotes]);

    const getVehicleCount = () => {
      let vehicleCount = 1;
      if (workflowData && Array.isArray(workflowData.step3)) {
        vehicleCount = workflowData.step3.length || 1;
      }
      return vehicleCount;
    };
    const getVehiclesCountById = (id: string) => {
      let data = JSON.stringify({
        policyDataBaseId: id,
      });
      axios({
        method: "post",
        url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/vehiclecount?policyDatabaseId=${id}&ncToken=${localStorage.getItem("NCToken")}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          "Content-Type": "Application/json",
        },
        data: data,
      })
        .then((response: any) => {
          const count = response.data?.length || 1;
          setVehicleCountFromApi(count);
          // Set vehicleCount in workflowData.step7 for PaymentScreen
        })
        .catch((error: any) => {
          if (error.response?.status === 401) {
            localStorage.clear();
            navigate("/");
          } else {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail:
                error.response && error.response.status
                  ? `Error ${error.response.status}: ${error.response.data?.message || "Unknown error while getting the vehicle count details!"}`
                  : "Unknown error while getting the vehicle count details!",
              life: 3000,
            });
          }
        });
    };
    // Calculate ancillary price for a given key
    const calculateAncillaryPrice = (key: AncillaryKey) => {
      const vehicleCount = isAncillaryFlow ? vehicleCountFromApi : getVehicleCount();
      if (!globalAncillaries[key]) return 0;
      switch (key) {
        case "wayRTG6Mon":
          return vehicleCount > 0 ? 99 + (vehicleCount - 1) * 50 : 0;
        case "wayRTG3Mon":
          return vehicleCount > 0 ? 69 + (vehicleCount - 1) * 35 : 0;
        case "wayRoadside6Mon":
          return vehicleCount > 0 ? 79 + (vehicleCount - 1) * 25 : 0;
        case "wayRoadside3Mon":
          return vehicleCount > 0 ? 59 + (vehicleCount - 1) * 25 : 0;
        default:
          return 0;
      }
    };

    // Calculate total due today
    const calculateTotalDueToday = (quote: InsuranceQuote) => {
      let total = quote.downPayment;
      total += calculateAncillaryPrice("wayRTG6Mon");
      total += calculateAncillaryPrice("wayRTG3Mon");
      total += calculateAncillaryPrice("wayRoadside6Mon");
      total += calculateAncillaryPrice("wayRoadside3Mon");
      if (globalAncillaries.hospitalIndIndividual) total += quote.hospitalIndIndividualPrice;
      if (globalAncillaries.hospitalIndFamily) total += quote.hospitalIndFamilyPrice;
      if (globalAncillaries.telemedicine) total += quote.telemedicinePrice;
      if (globalAncillaries.discountPackage) total += quote.discountPackagePrice;
      if (globalAncillaries.identityTheftInd) total += quote.identityTheftIndPrice;
      total += agencyFee;
      total += tranzpayServiceFee;
      return total;
    };
    const insertNote = async (insuredId: string, quoteId: string, note: string, taskId: string = "") => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_ACE_API_Url}/api/ACE/insertnote?ncToken=${localStorage.getItem("NCToken")}`,
          {
            insuredId,
            PolicyId: quoteId,
            note,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
              "Content-Type": "application/json",
            },
          },
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    };

    const getNotes = async (insuredId: string, quoteId: string) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ACE_API_Url}/api/ACE/notes?insuredId=${insuredId}&quoteId=${quoteId}&ncToken=${localStorage.getItem("NCToken")}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
            },
          },
        );
        return response.data.value.map((note: any) => ({
          text: note.subject,
          date: note.createDate,
          author: note.createdBy,
          id: note.databaseId,
        }));
      } catch (error) {
        throw error;
      }
    };

    const archiveNote = async (noteId: string) => {
      try {
        await axios.put(
          `${process.env.REACT_APP_ACE_API_Url}/api/ace/archivenote`,
          {
            ncToken: localStorage.getItem("NCToken"),
            noteDatabaseId: noteId,
            isArchive: true,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
              "Content-Type": "application/json",
            },
          },
        );
      } catch (error) {
        throw error;
      }
    };

    const loadNotes = async () => {
      setLoadingNotes(true);
      try {
        let insuredId = workflowData?.step1?.insuredId || workflowData?.insuredId;
        let quoteId = workflowData?.quoteId;
        if (!insuredId) {
          insuredId = localStorage.getItem("currentInsuredId");
        }
        if (isAncillaryFlow) {
          quoteId = selectedQuotes;
        }
        if (!quoteId) {
          const urlParams = new URLSearchParams(window.location.search);
          quoteId = workflowData?.quoteId || urlParams.get("transactionId");
        }
        if (insuredId && quoteId) {
          const fetchedNotes = await getNotes(insuredId, quoteId);
          if (fetchedNotes && Array.isArray(fetchedNotes)) {
            setNotes(fetchedNotes);
          }
        }
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load notes from API.",
          life: 3000,
        });
      } finally {
        setLoadingNotes(false);
      }
    };

    const saveQuoteHistory = async (selectedQuoteId: string) => {
      const contactInfo = workflowData?.step1 || {};
      const driverInfo = workflowData?.step2 || {};
      const vehicleInfo = workflowData?.step3 || {};
      const policyCoverageInfo = workflowData?.step4 || {};

      const data = JSON.stringify({
        transactionId: crypto.randomUUID(), // Generate a new transaction ID for ancillary flow
        clientName: contactInfo.firstName && contactInfo.lastName ? `${contactInfo.firstName} ${contactInfo.lastName}` : "",
        email: contactInfo.email || "",
        phone: contactInfo.phone || "",
        address: contactInfo.address || "",
        insuredId: insuredId,
        quoteId: selectedQuoteId, // Use the selected policy's databaseId as quoteId
        contactInformationJson: JSON.stringify(contactInfo),
        driverInformationJson: JSON.stringify(driverInfo),
        vehicleInformationJson: JSON.stringify(vehicleInfo),
        policyCoverageInformationJson: JSON.stringify(policyCoverageInfo),
        status: "In Progress",
        currentStep: String(currentStep || 7), // CompareQuotes is step 7
        quoteNumber: workflowData?.storedQuoteNumber || "",
        quoteType: "Ancillary Quote",
      });

      const config = {
        method: "post",
        url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/addorupdatequotehistory`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          "Content-Type": "application/json",
        },
        data: data,
      };

      try {
        await axios(config);
        // toast.current?.show({
        //   severity: "success",
        //   summary: "Success",
        //   detail: "Quote history saved successfully!",
        //   life: 3000,
        // });
      } catch (error: any) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Error saving quote history!",
          life: 3000,
        });
        throw error; // Re-throw to prevent proceeding if save fails
      }
    };
    const sortedQuotes = React.useMemo(() => {
      // If any carriers are expanded, preserve the order from visibleQuotes to maintain carrier grouping
      if (expandedCarriers.size > 0) {
        return quotes;
      }

      // Otherwise, apply sorting
      return [...quotes].sort((a, b) => {
        switch (sortBy) {
          case "price":
            return a.installmentPayment - b.installmentPayment;
          case "premium":
            return a.totalPremium - b.totalPremium;
          case "totalDueToday":
            return calculateTotalDueToday(a) - calculateTotalDueToday(b);
          case "totalAutoPremium":
            return a.totalPremium - b.totalPremium;
          default:
            return 0;
        }
      });
    }, [quotes, sortBy, globalAncillaries, agencyFee, tranzpayServiceFee, expandedCarriers]);

    // Detect ancillary flow from workflowData or localStorage

    // If ancillary, only show ancillary rows and select button
    const ancillaryFeatureRows = [
      { key: "ancillaryHeader", label: "Ancillaries ", type: "ancillaryHeader" },
      {
        key: "wayRTG6MonPrice",
        label: <span className="ancillary-label-indent">Way + RTG 6 Mon</span>,
        type: "ancillary",
        ancillaryKey: "wayRTG6Mon" as AncillaryKey,
      },
      {
        key: "wayRTG3MonPrice",
        label: <span className="ancillary-label-indent">Way + RTG 3 Mon</span>,
        type: "ancillary",
        ancillaryKey: "wayRTG3Mon" as AncillaryKey,
      },
      {
        key: "wayRoadside6MonPrice",
        label: <span className="ancillary-label-indent">Way + Roadside 6 Mon</span>,
        type: "ancillary",
        ancillaryKey: "wayRoadside6Mon" as AncillaryKey,
      },
      {
        key: "wayRoadside3MonPrice",
        label: <span className="ancillary-label-indent">Way + Roadside 3 Mon</span>,
        type: "ancillary",
        ancillaryKey: "wayRoadside3Mon" as AncillaryKey,
      },
      { key: "selectButton", label: "", type: "select" },
    ];
    const ancillaryFeatureRowsMemo = React.useMemo(() => {
      const totalAncillaries = ancillaryFeatureRows.length - 2;
      let ancillaryRowsToShow;
      if (showAllAncillaryRows || totalAncillaries <= 5) {
        ancillaryRowsToShow = ancillaryFeatureRows.slice(1, ancillaryFeatureRows.length - 1);
      } else {
        ancillaryRowsToShow = ancillaryFeatureRows.slice(1, 6); // first 5
      }
      // Add Show All/Collapse button to Ancillaries header only
      const ancillaryHeaderWithButton = {
        ...ancillaryFeatureRows[0],
        label: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", minWidth: 220 }}>
            <span style={{ fontWeight: 600, fontSize: "1em" }}>Ancillaries</span>
            {/* <button
              className="btn btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                setShowAllAncillaryRows((prev) => !prev);
              }}
              aria-label={showAllAncillaryRows ? "Collapse Ancillaries" : "Show All Ancillaries"}
            >
              {showAllAncillaryRows ? "Collapse" : "Show All"}
            </button> */}
          </div>
        ),
      };
      return [ancillaryHeaderWithButton, ...ancillaryRowsToShow];
    }, [showAllAncillaryRows]);

    const featureRows = isAncillaryFlow
      ? [
          ...ancillaryFeatureRowsMemo,
          { key: "transpayServiceFee", label: "Transpay Service Fee", type: "fee" },
          { key: "calculatedPrice", label: "Calculated Price", type: "calculatedPrice" },
          { key: "selectButton", label: "", type: "select" },
        ]
      : [
          { key: "totalPremium", label: "Total Premium", type: "currency" },
          { key: "downPayment", label: "Down Payment", type: "currency" },
          { key: "payments", label: "Payments", type: "text" },
          { key: "installmentPayment", label: "Installment Payment", type: "currency" },
          // Insert Ancillaries header and rows for full quote flow
          ...ancillaryFeatureRowsMemo,
          { key: "agencyFee", label: "Agency Fee", type: "fee" },
          { key: "transpayServiceFee", label: "Transpay Service Fee", type: "fee" },
          { key: "totalDueToday", label: "Total Due Today", type: "currency" },
          { key: "selectButton", label: "", type: "select" },
        ];

    // 3️⃣ Memoized tableRows
    const tableRows: TableRow[] = React.useMemo(
      () =>
        featureRows.map((row) => {
          const obj: TableRow = { feature: row.label, checkbox: null, key: row.key };

          if (row.type === "ancillaryHeader") {
            sortedQuotes.forEach((quote) => {
              obj[quote.id] = "";
            });
            obj["checkbox"] = null;
          }

          if (row.type === "ancillary" && row.ancillaryKey) {
            obj["checkbox"] = (
              <input
                key={globalAncillaries[row.ancillaryKey] ? "checked" : "unchecked"}
                type="checkbox"
                checked={globalAncillaries[row.ancillaryKey]}
                onChange={(e) => {
                  handleToggleAncillary(row.ancillaryKey);
                }}
                className="ancillary-checkbox"
                style={{ margin: "12px" }}
              />
            );
          }

          sortedQuotes.forEach((quote) => {
            if (row.key === "calculatedPrice") {
              let total = 0;
              total += calculateAncillaryPrice("wayRTG6Mon");
              total += calculateAncillaryPrice("wayRTG3Mon");
              total += calculateAncillaryPrice("wayRoadside6Mon");
              total += calculateAncillaryPrice("wayRoadside3Mon");
              obj[quote.id] = <span>${total}</span>;
            } else if (row.key === "totalDueToday") {
              obj[quote.id] = <span>${calculateTotalDueToday(quote)}</span>;
            } else if (row.type === "currency") {
              obj[quote.id] = <span>${(quote as any)[row.key]}</span>;
            } else if (row.type === "text") {
              obj[quote.id] = <span>{(quote as any)[row.key]}</span>;
            } else if (row.type === "fee") {
              obj[quote.id] = <span>{(quote as any)[row.key] > 0 ? `+$${(quote as any)[row.key]}` : "$0"}</span>;
            } else if (row.type === "ancillary" && row.ancillaryKey) {
              // Only show calculated value for checked ancillary, others zero
              const key = row.ancillaryKey as AncillaryKey;
              const isChecked = globalAncillaries[key];
              const value = isChecked ? calculateAncillaryPrice(key) : 0;
              obj[quote.id] = <span>${value}</span>;
            }
          });

          // Add price column data for ancillary flow
          if (isAncillaryFlow) {
            if (row.key === "calculatedPrice") {
              let total = 0;
              total += calculateAncillaryPrice("wayRTG6Mon");
              total += calculateAncillaryPrice("wayRTG3Mon");
              total += calculateAncillaryPrice("wayRoadside6Mon");
              total += calculateAncillaryPrice("wayRoadside3Mon");
              total += tranzpayServiceFee;
              obj["price"] = <span>${total}</span>;
            } else if (row.type === "ancillary" && row.ancillaryKey) {
              const key = row.ancillaryKey as AncillaryKey;
              const isChecked = globalAncillaries[key];
              const value = isChecked ? calculateAncillaryPrice(key) : 0;
              obj["price"] = <span>${value}</span>;
            } else if (row.type === "fee") {
              const feeValue = row.key === "transpayServiceFee" ? tranzpayServiceFee : 0;
              obj["price"] = <span>{feeValue > 0 ? `+$${feeValue}` : "$0"}</span>;
            } else if (row.type === "ancillaryHeader") {
              obj["price"] = "";
            }
          }

          if (row.type === "select") {
            sortedQuotes.forEach((quote) => {
              const isSelected = selectedQuotes === quote.id;
              obj[quote.id] = (
                <div style={{ display: "flex", gap: "12px", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                  <button
                    key={`select-btn-${quote.id}-${isSelected ? "selected" : "unselected"}`}
                    type="button"
                    className={`btn btn-primary select-btn${isSelected ? " selected" : ""}`}
                    onClick={() => {
                      if (!isSelected) {
                        getVehiclesCountById(quote.id);
                      }
                      setSelectedQuotes(isSelected ? null : quote.id);
                    }}
                  >
                    {isSelected ? "Selected" : "Select"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      if (quote.bridgeUrl) {
                        window.open(quote.bridgeUrl, "_blank");
                      }
                    }}
                    disabled={!quote.bridgeUrl}
                    style={{
                      opacity: quote.bridgeUrl ? 1 : 0.5,
                      cursor: quote.bridgeUrl ? "pointer" : "not-allowed",
                    }}
                  >
                    Bridge
                  </button>
                </div>
              );
            });

            // Add empty price cell for select button row in ancillary flow
            if (isAncillaryFlow) {
              obj["price"] = "";
            }
          }

          return obj;
        }),
      [featureRows, sortedQuotes, globalAncillaries, selectedQuotes, showAllAncillaryRows, vehicleCountFromApi],
    );

    // 4️⃣ Memoized tableColumns
    const tableColumns = React.useMemo(
      () => [
        {
          field: "feature",
          header: "Feature",
          body: (rowData: TableRow) => <span style={{ padding: "12px" }}>{rowData.feature}</span>,
          bodyClassName: "",
        },
        {
          field: "checkbox",
          header: "",
          body: (rowData: TableRow) => {
            // Render checkbox only for ancillary product rows
            if (rowData.checkbox !== null) {
              return <span>{rowData.checkbox}</span>;
            }
            return null;
          },
          bodyClassName: "",
        },
        // Conditionally include quote columns or price column based on ancillary flow
        ...(isAncillaryFlow === true
          ? [
              {
                field: "price",
                header: "Price",
                body: (rowData: TableRow) => {
                  const val = rowData["price"];
                  if (!val && val !== 0) return "";
                  return <span style={{ padding: "12px", display: "block" }}>{val}</span>;
                },
                bodyClassName: "",
              },
            ]
          : [...sortedQuotes]
              .sort((a, b) => {
                const aHasError = a.errors && a.errors.length > 0 ? 1 : 0;
                const bHasError = b.errors && b.errors.length > 0 ? 1 : 0;

                return aHasError - bHasError;
              })
              .map((quote) => {
                // Only apply background color if carrier is expanded
                const carrierBgColor = expandedCarriers.has(quote.carrier) ? carrierColors[quote.carrier] || "#ffffff" : "#ffffff";
                return {
                  field: quote.id,
                  header: (
                    <span className="flex flex-col items-center justify-center gap-2">
                      <span className="flex items-center gap-2">
                        <span className="logo logo-large">{quote.logo}</span>
                        <span>{quote.carrier}</span>
                        {/* Expand/Collapse button for multiple quotes from same carrier */}
                        {hasMultipleQuotes(quote.carrier) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCarrierExpansion(quote.carrier);
                            }}
                            style={{
                              background: "#2563eb",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 6px",
                              cursor: "pointer",
                              color: "white",
                              fontSize: "10px",
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title={expandedCarriers.has(quote.carrier) ? "Collapse quotes" : "Show all quotes"}
                          >
                            {expandedCarriers.has(quote.carrier) ? "▼" : "▶"}({quotesGroupedByCarrier[quote.carrier]?.length || 0})
                          </button>
                        )}
                        {/* Error icon */}
                        {quote.errors && quote.errors.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedQuoteErrors({
                                carrier: quote.carrier,
                                errors: quote.errors || [],
                                warnings: quote.warnings || [],
                              });
                              setShowErrorModal(true);
                            }}
                            style={{
                              border: "solid 1px #f87171",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "10px",
                            }}
                            title="View Errors"
                          >
                            ❌
                          </button>
                        )}
                        {/* Warning icon */}
                        {quote.warnings && quote.warnings.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedQuoteErrors({
                                carrier: quote.carrier,
                                errors: quote.errors || [],
                                warnings: quote.warnings || [],
                              });
                              setShowWarningModal(true);
                            }}
                            style={{
                              border: "solid 1px #f59e0b",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "#f59e0b",
                              fontWeight: "bold",
                              fontSize: "12px",
                            }}
                            title="View Warnings"
                          >
                            !
                          </button>
                        )}
                      </span>
                    </span>
                  ),
                  body: (rowData: TableRow) => {
                    // Check if this is an ancillaryHeader row
                    const isAncillaryHeader = featureRows.find((f) => f.label === rowData.feature)?.type === "ancillaryHeader";
                    const isSelected = selectedQuotes === quote.id;

                    const cellStyle = {
                      backgroundColor: isAncillaryHeader ? "transparent" : isSelected ? "#dbeafe" : carrierBgColor,
                      height: "100%",
                      // borderLeft: isSelected ? '3px solid #2563eb' : 'none',
                      // borderRight: isSelected ? '3px solid #2563eb' : 'none',
                    };

                    // Select button row
                    if (featureRows.find((f) => f.label === rowData.feature)?.type === "select") {
                      return (
                        <div
                          style={{
                            ...cellStyle,
                            display: "flex",
                            gap: "12px",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            padding: "12px",
                          }}
                        >
                          <button
                            type="button"
                            className={`btn btn-primary select-btn${isSelected ? " selected" : ""}`}
                            onClick={() => {
                              setSelectedQuotes(isSelected ? null : quote.id);
                            }}
                          >
                            {isSelected ? "Selected" : "Select"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              if (quote.bridgeUrl) {
                                window.open(quote.bridgeUrl, "_blank");
                              }
                            }}
                            disabled={!quote.bridgeUrl}
                            style={{
                              opacity: quote.bridgeUrl ? 1 : 0.5,
                              cursor: quote.bridgeUrl ? "pointer" : "not-allowed",
                            }}
                          >
                            Bridge
                          </button>
                        </div>
                      );
                    }
                    // Other rows - wrap in div with background color
                    return <div style={cellStyle}>{rowData[quote.id]}</div>;
                  },
                  bodyClassName: () => (selectedQuotes === quote.id ? "selected-column" : ""),
                };
              })),
      ],
      [sortedQuotes, selectedQuotes, globalAncillaries, featureRows, vehicleCountFromApi, expandedCarriers, quotesGroupedByCarrier],
    );

    const handleAddNote = async () => {
      if (noteValue.trim()) {
        const newNote = noteValue.trim();
        setNoteValue("");
        setShowNoteModal(false);
        // Call API to insert note
        try {
          let insuredId = workflowData?.step1?.insuredId || workflowData?.insuredId;
          let quoteId = workflowData?.quoteId;
          if (!insuredId) {
            insuredId = localStorage.getItem("currentInsuredId");
          }
          if (isAncillaryFlow) {
            quoteId = selectedQuotes;
          }
          if (!quoteId) {
            const urlParams = new URLSearchParams(window.location.search);
            quoteId = workflowData?.quoteId || urlParams.get("transactionId");
          }
          if (insuredId && quoteId) {
            await insertNote(insuredId, quoteId, newNote);
            await loadNotes(); // Refetch notes to include the new note with id
          }
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to save note to API. Please try again.",
            life: 3000,
          });
        }
      }
    };
    const handleToggleAncillary = (key: keyof typeof globalAncillaries) => {
      setGlobalAncillaries((prev: typeof globalAncillaries) => {
        return { ...prev, [key]: !prev[key] };
      });
    };

    const handleEditNote = (idx: number) => {
      setEditNoteIdx(idx);
      setNoteValue(notes[idx].text);
      setShowNoteModal(true);
    };

    const handleSaveEditNote = async () => {
      if (editNoteIdx !== null && noteValue.trim()) {
        const updatedNote = noteValue.trim();
        setNotes(notes.map((n, i) => (i === editNoteIdx ? { ...n, text: updatedNote, date: new Date().toISOString() } : n)));
        setEditNoteIdx(null);
        setNoteValue("");
        setShowNoteModal(false);
        try {
          let insuredId = workflowData?.step1?.insuredId || workflowData?.insuredId;
          let quoteId = workflowData?.quoteId;
          if (!insuredId) {
            insuredId = localStorage.getItem("currentInsuredId");
          }
          if (isAncillaryFlow) {
            quoteId = selectedQuotes;
          }
          if (!quoteId) {
            const urlParams = new URLSearchParams(window.location.search);
            quoteId = urlParams.get("transactionId") || workflowData?.quoteId;
          }
          if (insuredId && quoteId) {
            await insertNote(insuredId, quoteId, updatedNote);
          }
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to save updated note to API. Please try again.",
            life: 3000,
          });
        }
      }
    };

    const handleCancelEditNote = () => {
      setEditNoteIdx(null);
      setNoteValue("");
      setShowNoteModal(false);
    };

    const onSendEmail = () => {
      if (!selectedQuotes) {
        setSendStatus("Please select a quote to send.");
        return;
      }
      if (sendMethod === "email") {
        if (!receiverEmail.trim()) {
          setSendStatus("Please enter a receiver email address.");
          return;
        }
      } else {
        if (!recipient.trim()) {
          setSendStatus("Please enter a valid recipient.");
          return;
        }
      }

      const insuredName = [workflowData?.step1?.firstName, workflowData?.step1?.lastName].filter(Boolean).join(" ") || "Insured";

      let htmlBody = "";
      if (isAncillaryFlow) {
        const selectedCard = ancillaryQuoteCards.find((c) => c.id === selectedQuotes);
        const ancillaryHtmlRows: [string, number][] = [];
        if (globalAncillaries.wayRTG6Mon) ancillaryHtmlRows.push(["Way + RTG 6 Mon", calculateAncillaryPrice("wayRTG6Mon")]);
        if (globalAncillaries.wayRTG3Mon) ancillaryHtmlRows.push(["Way + RTG 3 Mon", calculateAncillaryPrice("wayRTG3Mon")]);
        if (globalAncillaries.wayRoadside6Mon) ancillaryHtmlRows.push(["Way + Roadside 6 Mon", calculateAncillaryPrice("wayRoadside6Mon")]);
        if (globalAncillaries.wayRoadside3Mon) ancillaryHtmlRows.push(["Way + Roadside 3 Mon", calculateAncillaryPrice("wayRoadside3Mon")]);
        const totalAncillaryPrice = ancillaryHtmlRows.reduce((sum, [, price]) => sum + price, 0);

        const ancillaryHtmlSection =
          ancillaryHtmlRows.length > 0
            ? `<tr style="background:#eff6ff;"><td colspan="2" style="padding:6px 14px;color:#2563eb;font-weight:700;font-size:12px;letter-spacing:0.06em;">ANCILLARIES</td></tr>
${ancillaryHtmlRows.map(([label, price], i) => `  <tr${i % 2 === 1 ? ' style="background:#f1f5f9;"' : ""}><td style="padding:8px 14px 8px 22px;">${label}</td><td style="padding:8px 14px;">$${(price as number).toFixed(2)}</td></tr>`).join("\n")}
  <tr style="background:#f1f5f9;"><td style="padding:8px 14px;font-weight:700;">Total Ancillary Price</td><td style="padding:8px 14px;font-weight:700;">$${totalAncillaryPrice.toFixed(2)}</td></tr>`
            : "";

        htmlBody = `<html><body style="font-family:Arial,sans-serif;color:#334155;padding:24px;">
<p><strong>To:</strong> ${receiverEmail}</p>
<p>Dear ${insuredName},</p>
<p>Please find your selected policy details below:</p>
<table border="1" cellpadding="10" cellspacing="0" style="border-collapse:collapse;min-width:320px;font-size:14px;">
  <tr style="background:#2563eb;color:#ffffff;"><th style="text-align:left;padding:10px 14px;">Field</th><th style="text-align:left;padding:10px 14px;">Value</th></tr>
  <tr><td style="padding:8px 14px;">Policy #</td><td style="padding:8px 14px;">${selectedCard?.policyNumber || "N/A"}</td></tr>
  <tr style="background:#f1f5f9;"><td style="padding:8px 14px;">Effective Date</td><td style="padding:8px 14px;">${selectedCard?.effectiveDate || "N/A"}</td></tr>
  <tr><td style="padding:8px 14px;">Total Premium</td><td style="padding:8px 14px;">$${Number(selectedCard?.totalPremium || 0).toFixed(2)}</td></tr>
  ${ancillaryHtmlSection}
</table>
${emailBody.trim() ? `<p style="margin-top:16px;white-space:pre-wrap;">${emailBody.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>` : ""}
<p>If you have any questions, please contact us.</p>
<p>Thank you!</p>
</body></html>`;
      } else if (selectedEmailQuote) {
        const q = selectedEmailQuote;
        // Build ancillary rows for HTML email
        const ancillaryHtmlRows: [string, number][] = [];
        if (globalAncillaries.wayRTG6Mon) ancillaryHtmlRows.push(["Way + RTG 6 Mon", calculateAncillaryPrice("wayRTG6Mon")]);
        if (globalAncillaries.wayRTG3Mon) ancillaryHtmlRows.push(["Way + RTG 3 Mon", calculateAncillaryPrice("wayRTG3Mon")]);
        if (globalAncillaries.wayRoadside6Mon) ancillaryHtmlRows.push(["Way + Roadside 6 Mon", calculateAncillaryPrice("wayRoadside6Mon")]);
        if (globalAncillaries.wayRoadside3Mon) ancillaryHtmlRows.push(["Way + Roadside 3 Mon", calculateAncillaryPrice("wayRoadside3Mon")]);
        if (globalAncillaries.hospitalIndIndividual) ancillaryHtmlRows.push(["Hospital Ind (Individual)", q.hospitalIndIndividualPrice]);
        if (globalAncillaries.hospitalIndFamily) ancillaryHtmlRows.push(["Hospital Ind (Family)", q.hospitalIndFamilyPrice]);
        if (globalAncillaries.telemedicine) ancillaryHtmlRows.push(["Telemedicine", q.telemedicinePrice]);
        if (globalAncillaries.discountPackage) ancillaryHtmlRows.push(["Discount Package", q.discountPackagePrice]);
        if (globalAncillaries.identityTheftInd) ancillaryHtmlRows.push(["Identity Theft (Ind)", q.identityTheftIndPrice]);

        const ancillaryHtmlSection =
          ancillaryHtmlRows.length > 0
            ? `<tr style="background:#eff6ff;"><td colspan="2" style="padding:6px 14px;color:#2563eb;font-weight:700;font-size:12px;letter-spacing:0.06em;">ANCILLARIES</td></tr>
${ancillaryHtmlRows.map(([label, price], i) => `  <tr${i % 2 === 1 ? ' style="background:#f1f5f9;"' : ""}><td style="padding:8px 14px 8px 22px;">${label}</td><td style="padding:8px 14px;">$${price.toFixed(2)}</td></tr>`).join("\n")}`
            : "";

        const totalDueToday = calculateTotalDueToday(q);

        htmlBody = `<html><body style="font-family:Arial,sans-serif;color:#334155;padding:24px;">
<p><strong>To:</strong> ${receiverEmail}</p>
<p>Dear ${insuredName},</p>
<p>Please find your selected quote details below:</p>
<table border="1" cellpadding="10" cellspacing="0" style="border-collapse:collapse;min-width:320px;font-size:14px;">
  <tr style="background:#2563eb;color:#ffffff;"><th style="text-align:left;padding:10px 14px;">Field</th><th style="text-align:left;padding:10px 14px;">Value</th></tr>
  <tr><td style="padding:8px 14px;">Carrier</td><td style="padding:8px 14px;">${q.carrier}</td></tr>
  <tr style="background:#f1f5f9;"><td style="padding:8px 14px;">Total Premium</td><td style="padding:8px 14px;">$${q.totalPremium.toFixed(2)}</td></tr>
  <tr><td style="padding:8px 14px;">Down Payment</td><td style="padding:8px 14px;">$${q.downPayment.toFixed(2)}</td></tr>
  <tr style="background:#f1f5f9;"><td style="padding:8px 14px;">Installment Payment</td><td style="padding:8px 14px;">$${q.installmentPayment.toFixed(2)}</td></tr>
  <tr><td style="padding:8px 14px;">Number of Payments</td><td style="padding:8px 14px;">${q.payments}</td></tr>
  ${ancillaryHtmlSection}
  <tr style="background:#f1f5f9;"><td style="padding:8px 14px;">Agency Fee</td><td style="padding:8px 14px;">$${q.agencyFee.toFixed(2)}</td></tr>
  <tr><td style="padding:8px 14px;">Transpay Service Fee</td><td style="padding:8px 14px;">$${q.transpayServiceFee.toFixed(2)}</td></tr>
  <tr style="background:#f1f5f9;"><td style="padding:8px 14px;font-weight:700;">Total Due Today</td><td style="padding:8px 14px;font-weight:700;">$${totalDueToday.toFixed(2)}</td></tr>
</table>
${emailBody.trim() ? `<p style="margin-top:16px;white-space:pre-wrap;">${emailBody.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>` : ""}
<p>If you have any questions, please contact us.</p>
<p>Thank you!</p>
</body></html>`;
      }

      setIsSendingEmail(true);
      let data = JSON.stringify({
        Receiver: receiverEmail,
        Subject: `Quote Details - ${workflowData?.step1?.firstName || ""} ${workflowData?.step1?.lastName || ""}`,
        Body: htmlBody,
        NcToken: localStorage.getItem("NCToken"),
      });
      axios({
        method: "post",
        url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/emailRequest`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          "Content-Type": "Application/json",
        },
        data: data,
      })
        .then((response: any) => {
          setIsSendingEmail(false);
          setShowSendModal(false);
          setSenderEmail("");
          setReceiverEmail("");
          setEmailBody("");
          setRecipient("");
          setSendStatus(null);
          setSelectedEmailQuote(null);
          toast.current?.show({
            severity: "success",
            summary: "Email Sent",
            detail: "Quote details sent successfully!",
            life: 3000,
          });
        })
        .catch((error: any) => {
          setIsSendingEmail(false);
          if (error.response?.status === 401) {
            localStorage.clear();
            navigate("/");
          } else {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail:
                error.response && error.response.status
                  ? `Error ${error.response.status}: ${error.response.data?.message || "Unknown error while sending the email!"}`
                  : "Unknown error while sending the email!",
              life: 3000,
            });
          }
        });
    };

    // Removed handleSelectQuote, now handled inline in select button
    // Transpay modal
    const renderTranspayModal = () =>
      showTranspayModal && (
        <div className="notes-modal-overlay modal-center">
          <div className="notes-modal">
            <h2>Bridge - {transpayCarrier}</h2>
            <button className="modal-close-btn" onClick={() => setShowTranspayModal(false)} aria-label="Close transpay modal">
              ×
            </button>
            {bridgeUrl ? (
              <div style={{ marginBottom: "18px" }}>
                <p style={{ marginBottom: "12px", fontSize: "1.05em", color: "#334155" }}>Click the link below to open the Bridge URL:</p>
                <a
                  href={bridgeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    padding: "10px 16px",
                    background: "#3b82f6",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "4px",
                    fontWeight: "500",
                    fontSize: "0.95em",
                  }}
                >
                  Open Bridge URL
                </a>
              </div>
            ) : (
              <div style={{ marginBottom: "18px", fontSize: "1.05em", color: "#334155" }}>No Bridge URL available for this quote.</div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowTranspayModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      );

    // Error modal
    const renderErrorModal = () =>
      showErrorModal &&
      selectedQuoteErrors && (
        <div className="notes-modal-overlay modal-center">
          <div className="notes-modal">
            <h2>Quote Errors - {selectedQuoteErrors.carrier}</h2>
            <button className="modal-close-btn" onClick={() => setShowErrorModal(false)} aria-label="Close error modal">
              ×
            </button>
            <div style={{ marginBottom: "18px" }}>
              {selectedQuoteErrors.errors && selectedQuoteErrors.errors.length > 0 && (
                <div style={{ marginBottom: "16px" }}>
                  <ul style={{ listStyle: "none", padding: 0 }}>
                    {selectedQuoteErrors.errors.map((error, index) => (
                      <li
                        key={index}
                        style={{
                          padding: "4px 12px",
                          marginBottom: "6px",
                          backgroundColor: "#fee2e2",
                          border: "1px solid #fecaca",
                          borderRadius: "4px",
                          color: "#991b1b",
                          fontSize: "12px",
                        }}
                      >
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      );

    // Warning modal
    const renderWarningModal = () =>
      showWarningModal &&
      selectedQuoteErrors && (
        <div className="notes-modal-overlay modal-center">
          <div className="notes-modal">
            <h2>Quote Warnings - {selectedQuoteErrors.carrier}</h2>
            <button className="modal-close-btn" onClick={() => setShowWarningModal(false)} aria-label="Close warning modal">
              ×
            </button>
            <div style={{ marginBottom: "18px" }}>
              {selectedQuoteErrors.warnings && selectedQuoteErrors.warnings.length > 0 && (
                <div style={{ marginBottom: "16px" }}>
                  <ul style={{ listStyle: "none", padding: 0 }}>
                    {selectedQuoteErrors.warnings.map((warning, index) => (
                      <li
                        key={index}
                        style={{
                          padding: "4px 12px",
                          marginBottom: "6px",
                          backgroundColor: "#fef3c7",
                          border: "1px solid #fde68a",
                          borderRadius: "4px",
                          color: "#92400e",
                          fontSize: "12px",
                        }}
                      >
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      );

    return (
      <div>
        <div className="page-container">
          <Toast ref={toast} />
          {renderTranspayModal()}
          {renderErrorModal()}
          {renderWarningModal()}
          {/* Ancillary quote cards above DataTable */}
          <div className="page-header flex items-center justify-between">
            <div className="">
              <h1 className="text-2xl font-bold">Compare Insurance Quotes</h1>
              <p className="text-sm text-gray-500">Compare auto insurance quotes from top carriers</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="header-btn-group flex items-center gap-2">
                {/* Sort Option Dropdown */}
                {(!isAncillaryFlow || (isAncillaryFlow && selectedQuotes)) && (
                  <>
                    <button
                      className="btn-icon btn-add-note"
                      title="Add Note"
                      onClick={() => {
                        setEditNoteIdx(null);
                        setNoteValue("");
                        setShowNoteModal(true);
                      }}
                    >
                      {/* Add Note Icon (plus) */}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                    <button
                      className="btn-icon btn-history"
                      title="See History"
                      onClick={async () => {
                        setShowNotesDrawer(true);
                        await loadNotes();
                      }}
                    >
                      {/* History Icon (clock) */}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="2" />
                        <path d="M12 8v4l3 2" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </>
                )}

                <button
                  className="btn-icon btn-email"
                  title="Send via Email"
                  onClick={() => {
                    if (isAncillaryFlow && !selectedQuotes) {
                      toast.current?.show({
                        severity: "warn",
                        summary: "Selection Required",
                        detail: "Please select a policy before sending the email.",
                        life: 3000,
                      });
                      return;
                    }
                    const insuredEmail = workflowData?.step1?.email || "";
                    if (!isAncillaryFlow) {
                      const selectedQ = quotes.find((q) => q.id === selectedQuotes) || null;
                      setSelectedEmailQuote(selectedQ);
                    }
                    setReceiverEmail(insuredEmail);
                    setSendMethod("email");
                    setSendStatus(null);
                    setShowSendModal(true);
                  }}
                >
                  {/* Email Icon */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="7" width="18" height="10" rx="2" stroke="#2563eb" strokeWidth="2" />
                    <path d="M3 7l9 6 9-6" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  className="btn-icon btn-text"
                  title="Send via Text"
                  onClick={() => {
                    setSendMethod("text");
                    setShowSendModal(true);
                  }}
                >
                  {/* Text/SMS Icon (chat bubble) */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                      stroke="#2563eb"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {isAncillaryFlow && (
            <div style={{ display: "flex", gap: "18px", margin: "24px 10px 16px ", overflowX: "auto", whiteSpace: "nowrap", paddingBottom: "8px" }}>
              {loadingAncillaryCards ? (
                <div className="driver-loading-overlay">
                  <div className="driver-loading-content">
                    <div className="driver-loading-spinner" />
                    <p className="driver-loading-text">Loading ...</p>
                  </div>
                </div>
              ) : (
                ancillaryQuoteCards.map((q) => (
                  <div
                    key={q.id}
                    className="quote-card"
                    onClick={() => {
                      if (selectedQuotes !== q.id) {
                        getVehiclesCountById(q.id);
                      }
                      setSelectedQuotes(q.id);
                    }}
                  >
                    <div className="quote-items">Policy #: {q.policyNumber || "N/A"}</div>
                    <div className="quote-items">Effective: {q.effectiveDate || "N/A"}</div>
                    <div className="quote-items">Total Premium: ${q.totalPremium}</div>
                    <div style={{ marginTop: 8 }}>
                      <button
                        className={selectedQuotes === q.id ? "btn btn-primary" : "btn btn-secondary"}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedQuotes !== q.id) {
                            getVehiclesCountById(q.id);
                          }
                          setSelectedQuotes(q.id);
                        }}
                      >
                        {selectedQuotes === q.id ? "Selected" : "Select"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {/* Header: If ancillary, show normal header and sort options */}

          <div className="text-end m-2">
            <label htmlFor="sortBy" className="font-semibold text-gray-700 ml-2">
              Sort by:
            </label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="form-input compact-select"
              style={{ minWidth: 160 }}
            >
              <option value="">Select</option>
              <option value="price">Installment Payment</option>
              <option value="premium">Total Premium</option>
              <option value="totalDueToday">Total Due Today (All Fees & Ancillaries)</option>
              <option value="totalAutoPremium">Total Auto Premium</option>
            </select>
          </div>
          <div style={{ position: "relative" }}>
            {quotesLoading && (
              <div className="driver-loading-overlay">
                <div className="driver-loading-content">
                  <div className="driver-loading-spinner" />
                  <p className="driver-loading-text">Loading quotes...</p>
                </div>
              </div>
            )}
            <DataTable
              value={tableRows}
              dataKey="key"
              key={
                (selectedQuotes || "") +
                "-" +
                showAllAncillaryRows +
                "-" +
                JSON.stringify(globalAncillaries) +
                "-" +
                vehicleCountFromApi +
                "-" +
                Array.from(expandedCarriers).join(",") +
                "-" +
                sortBy
              }
              scrollable
              scrollHeight="550px"
              className="compare-quotes custom-datatable fixed-header-table"
              showGridlines
              responsiveLayout="scroll"
              rowClassName={(rowData: any) => (rowData.key === "selectButton" ? "sticky-select-row" : "")}
            >
              {tableColumns.map((col, idx) => (
                <Column
                  key={col.field || idx}
                  field={col.field}
                  header={col.header}
                  body={col.body}
                  bodyClassName={col.bodyClassName}
                  frozen={idx < 2}
                  style={idx === 0 ? { minWidth: "200px" } : idx === 1 ? { minWidth: "60px" } : undefined}
                />
              ))}
            </DataTable>
          </div>
          {showSendModal && (
            <div className="notes-modal-overlay modal-center" style={{ minWidth: "600px" }}>
              <div className="notes-modal">
                <h2>Send Selected Quotes via {sendMethod === "email" ? "Email" : "Text"}</h2>
                <button
                  className="modal-close-btn"
                  onClick={() => {
                    setShowSendModal(false);
                    setSenderEmail("");
                    setReceiverEmail("");
                    setEmailBody("");
                    setRecipient("");
                    setSendStatus(null);
                    setIsSendingEmail(false);
                    setSelectedEmailQuote(null);
                  }}
                  aria-label="Close send modal"
                >
                  ×
                </button>
                {!isAncillaryFlow && (
                  <div style={{ marginBottom: "12px" }}>
                    <strong>Selected Quote:</strong>
                    {!selectedQuotes ? (
                      <span style={{ color: "#b91c1c", marginLeft: 8 }}>None selected</span>
                    ) : (
                      <ul style={{ margin: "8px 0 0 0", paddingLeft: 18 }}>
                        {(() => {
                          const q = quotes.find((q) => q.id === selectedQuotes);
                          return <li style={{ fontSize: "0.95em", color: "#2563eb" }}>{q ? `${q.carrier} (${q.logo})` : selectedQuotes}</li>;
                        })()}
                      </ul>
                    )}
                  </div>
                )}
                {sendMethod === "email" ? (
                  <>
                    {(!selectedQuotes || sendStatus) && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          backgroundColor: "#fee2e2",
                          border: "1px solid #fecaca",
                          borderRadius: "6px",
                          padding: "10px 14px",
                          marginBottom: "14px",
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                          <circle cx="12" cy="12" r="10" stroke="#b91c1c" strokeWidth="2" />
                          <path d="M12 8v4M12 16h.01" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        {!selectedQuotes && (
                          <span style={{ color: "#b91c1c", fontSize: "0.875em", fontWeight: 500 }}>
                            No quote selected. Please select a quote before sending the email.
                          </span>
                        )}
                        {sendStatus && <span style={{ color: "#b91c1c", fontSize: "0.875em", fontWeight: 500 }}>{sendStatus}</span>}
                      </div>
                    )}{" "}
                    <div style={{ marginBottom: "12px" }}>
                      <label style={{ fontWeight: 500, fontSize: "1em", color: "#334155" }}>Receiver Email:</label>
                      <input
                        type="email"
                        className="form-input"
                        placeholder="Enter receiver email address"
                        value={receiverEmail}
                        onChange={(e) => setReceiverEmail(e.target.value)}
                      />
                    </div>
                    {(isAncillaryFlow ? !!selectedQuotes : !!selectedEmailQuote) && (
                      <div style={{ marginBottom: "12px" }}>
                        <label style={{ fontWeight: 500, fontSize: "1em", color: "#334155" }}>Email Body Preview:</label>
                        <div style={{ marginTop: 8, overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875em" }}>
                            <thead>
                              <tr style={{ background: "#2563eb", color: "#fff" }}>
                                <th style={{ textAlign: "left", padding: "8px 12px" }}>Field</th>
                                <th style={{ textAlign: "left", padding: "8px 12px" }}>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isAncillaryFlow ? (
                                (() => {
                                  const card = ancillaryQuoteCards.find((c) => c.id === selectedQuotes);
                                  const ancRows: [string, number][] = [];
                                  if (globalAncillaries.wayRTG6Mon) ancRows.push(["Way + RTG 6 Mon", calculateAncillaryPrice("wayRTG6Mon")]);
                                  if (globalAncillaries.wayRTG3Mon) ancRows.push(["Way + RTG 3 Mon", calculateAncillaryPrice("wayRTG3Mon")]);
                                  if (globalAncillaries.wayRoadside6Mon)
                                    ancRows.push(["Way + Roadside 6 Mon", calculateAncillaryPrice("wayRoadside6Mon")]);
                                  if (globalAncillaries.wayRoadside3Mon)
                                    ancRows.push(["Way + Roadside 3 Mon", calculateAncillaryPrice("wayRoadside3Mon")]);
                                  const totalAncillaryPrice = ancRows.reduce((sum, [, price]) => sum + price, 0);
                                  return (
                                    <>
                                      {[
                                        ["Policy #", card?.policyNumber || "N/A"],
                                        ["Effective Date", card?.effectiveDate || "N/A"],
                                        ["Total Premium", `$${Number(card?.totalPremium || 0).toFixed(2)}`],
                                      ].map(([field, value], i) => (
                                        <tr key={field} style={{ background: i % 2 === 0 ? "#f1f5f9" : "#fff" }}>
                                          <td style={{ padding: "7px 12px", color: "#475569", fontWeight: 500 }}>{field}</td>
                                          <td style={{ padding: "7px 12px", color: "#1e293b" }}>{value}</td>
                                        </tr>
                                      ))}
                                      {ancRows.length > 0 && (
                                        <>
                                          <tr style={{ background: "#eff6ff" }}>
                                            <td
                                              colSpan={2}
                                              style={{
                                                padding: "6px 12px",
                                                color: "#2563eb",
                                                fontWeight: 700,
                                                fontSize: "0.78em",
                                                letterSpacing: "0.06em",
                                              }}
                                            >
                                              ANCILLARIES
                                            </td>
                                          </tr>
                                          {ancRows.map(([label, price], i) => (
                                            <tr key={label} style={{ background: i % 2 === 0 ? "#fff" : "#f1f5f9" }}>
                                              <td style={{ padding: "7px 12px 7px 20px", color: "#475569", fontWeight: 500 }}>{label}</td>
                                              <td style={{ padding: "7px 12px", color: "#1e293b" }}>${(price as number).toFixed(2)}</td>
                                            </tr>
                                          ))}
                                          <tr style={{ background: "#f1f5f9" }}>
                                            <td style={{ padding: "7px 12px", color: "#475569", fontWeight: 700 }}>Total Ancillary Price</td>
                                            <td style={{ padding: "7px 12px", color: "#1e293b", fontWeight: 700 }}>
                                              ${totalAncillaryPrice.toFixed(2)}
                                            </td>
                                          </tr>
                                        </>
                                      )}
                                    </>
                                  );
                                })()
                              ) : selectedEmailQuote ? (
                                <>
                                  {/* Quote detail rows */}
                                  {[
                                    ["Carrier", selectedEmailQuote.carrier],
                                    ["Total Premium", `$${selectedEmailQuote.totalPremium.toFixed(2)}`],
                                    ["Down Payment", `$${selectedEmailQuote.downPayment.toFixed(2)}`],
                                    ["Installment Payment", `$${selectedEmailQuote.installmentPayment.toFixed(2)}`],
                                    ["Number of Payments", String(selectedEmailQuote.payments)],
                                  ].map(([field, value], i) => (
                                    <tr key={field} style={{ background: i % 2 === 0 ? "#f1f5f9" : "#fff" }}>
                                      <td style={{ padding: "7px 12px", color: "#475569", fontWeight: 500 }}>{field}</td>
                                      <td style={{ padding: "7px 12px", color: "#1e293b" }}>{value}</td>
                                    </tr>
                                  ))}
                                  {/* Ancillary rows */}
                                  {(() => {
                                    const ancRows: [string, number][] = [];
                                    if (globalAncillaries.wayRTG6Mon) ancRows.push(["Way + RTG 6 Mon", calculateAncillaryPrice("wayRTG6Mon")]);
                                    if (globalAncillaries.wayRTG3Mon) ancRows.push(["Way + RTG 3 Mon", calculateAncillaryPrice("wayRTG3Mon")]);
                                    if (globalAncillaries.wayRoadside6Mon)
                                      ancRows.push(["Way + Roadside 6 Mon", calculateAncillaryPrice("wayRoadside6Mon")]);
                                    if (globalAncillaries.wayRoadside3Mon)
                                      ancRows.push(["Way + Roadside 3 Mon", calculateAncillaryPrice("wayRoadside3Mon")]);
                                    if (globalAncillaries.hospitalIndIndividual)
                                      ancRows.push(["Hospital Ind (Individual)", selectedEmailQuote.hospitalIndIndividualPrice]);
                                    if (globalAncillaries.hospitalIndFamily)
                                      ancRows.push(["Hospital Ind (Family)", selectedEmailQuote.hospitalIndFamilyPrice]);
                                    if (globalAncillaries.telemedicine) ancRows.push(["Telemedicine", selectedEmailQuote.telemedicinePrice]);
                                    if (globalAncillaries.discountPackage)
                                      ancRows.push(["Discount Package", selectedEmailQuote.discountPackagePrice]);
                                    if (globalAncillaries.identityTheftInd)
                                      ancRows.push(["Identity Theft (Ind)", selectedEmailQuote.identityTheftIndPrice]);
                                    if (ancRows.length === 0) return null;
                                    return (
                                      <>
                                        <tr style={{ background: "#eff6ff" }}>
                                          <td
                                            colSpan={2}
                                            style={{
                                              padding: "6px 12px",
                                              color: "#2563eb",
                                              fontWeight: 700,
                                              fontSize: "0.78em",
                                              letterSpacing: "0.06em",
                                            }}
                                          >
                                            ANCILLARIES
                                          </td>
                                        </tr>
                                        {ancRows.map(([label, price], i) => (
                                          <tr key={label} style={{ background: i % 2 === 0 ? "#fff" : "#f1f5f9" }}>
                                            <td style={{ padding: "7px 12px 7px 20px", color: "#475569", fontWeight: 500 }}>{label}</td>
                                            <td style={{ padding: "7px 12px", color: "#1e293b" }}>${price.toFixed(2)}</td>
                                          </tr>
                                        ))}
                                      </>
                                    );
                                  })()}
                                  {/* Fees & Total */}
                                  {[
                                    ["Agency Fee", `$${selectedEmailQuote.agencyFee.toFixed(2)}`],
                                    ["Transpay Service Fee", `$${selectedEmailQuote.transpayServiceFee.toFixed(2)}`],
                                    ["Total Due Today", `$${calculateTotalDueToday(selectedEmailQuote).toFixed(2)}`],
                                  ].map(([field, value], i) => (
                                    <tr key={field} style={{ background: i % 2 === 0 ? "#f1f5f9" : "#fff" }}>
                                      <td style={{ padding: "7px 12px", color: "#475569", fontWeight: field === "Total Due Today" ? 700 : 500 }}>
                                        {field}
                                      </td>
                                      <td style={{ padding: "7px 12px", color: "#1e293b", fontWeight: field === "Total Due Today" ? 700 : 400 }}>
                                        {value}
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              ) : null}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    <div style={{ marginBottom: "12px" }}>
                      <label style={{ fontWeight: 500, fontSize: "1em", color: "#334155" }}>
                        Additional Message <span style={{ fontWeight: 400, color: "#94a3b8" }}>(optional)</span>:
                      </label>
                      <textarea
                        className="form-input"
                        placeholder="Add any extra details or message here..."
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        rows={3}
                        style={{ resize: "vertical", minHeight: 72 }}
                      />
                    </div>
                  </>
                ) : (
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ fontWeight: 500, fontSize: "1em", color: "#334155" }}>Recipient Phone Number:</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter phone number"
                      value={recipient}
                      onChange={(e) => setRecipient(formatPhoneNumberDash(e.target.value))}
                    />
                  </div>
                )}
                <div className="modal-actions">
                  <button
                    className="btn btn-primary"
                    disabled={!selectedQuotes || isSendingEmail}
                    style={{
                      opacity: !selectedQuotes || isSendingEmail ? 0.5 : 1,
                      cursor: !selectedQuotes || isSendingEmail ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                    onClick={() => {
                      onSendEmail();
                    }}
                  >
                    {isSendingEmail && (
                      <span
                        className="driver-loading-spinner"
                        style={{ width: 16, height: 16, borderWidth: 2, display: "inline-block", flexShrink: 0, marginBottom: 0 }}
                      />
                    )}
                    {isSendingEmail ? "Sending..." : "Send"}
                  </button>
                  <button
                    className="btn btn-secondary"
                    disabled={isSendingEmail}
                    onClick={() => {
                      setShowSendModal(false);
                      setSenderEmail("");
                      setReceiverEmail("");
                      setEmailBody("");
                      setRecipient("");
                      setSendStatus(null);
                      setIsSendingEmail(false);
                      setSelectedEmailQuote(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Only use unified modal below. Remove old showNotesModal block. */}
          {showNoteModal && (
            <div className="notes-modal-overlay modal-center">
              <div className="notes-modal">
                <h2>{editNoteIdx !== null ? "Edit Note" : "Add Note"}</h2>
                <button className="modal-close-btn" onClick={handleCancelEditNote} aria-label="Close note modal">
                  ×
                </button>
                <div style={{ marginBottom: "12px" }}>
                  <label style={{ fontWeight: 500, fontSize: "1em", color: "#334155" }}>
                    {editNoteIdx !== null ? "Edit your note:" : "Add a note about these quotes:"}
                  </label>
                  <textarea
                    className="form-input"
                    placeholder={editNoteIdx !== null ? "Edit your note..." : "Add a note about these quotes..."}
                    value={noteValue}
                    onChange={(e) => setNoteValue(e.target.value)}
                  />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={editNoteIdx !== null ? handleSaveEditNote : handleAddNote}>
                    Save
                  </button>
                  <button className="btn btn-secondary" onClick={handleCancelEditNote}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Notes Drawer (side panel) */}
          {showNotesDrawer && (
            <div
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                height: "100vh",
                width: "400px",
                background: "#f8fafc",
                boxShadow: "-4px 0 24px rgba(54,192,196,0.18)",
                zIndex: 1100,
                display: "flex",
                flexDirection: "column",
                padding: "32px 28px",
                overflowY: "auto",
                borderTopLeftRadius: "18px",
                borderBottomLeftRadius: "18px",
                borderLeft: "2px solid #36c0c4",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
                <h2 style={{ fontSize: "1.2em", fontWeight: 700, color: "#2563eb", letterSpacing: "0.02em" }}>Notes History</h2>
                <button
                  style={{
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    fontSize: "1.2em",
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(37,99,235,0.10)",
                  }}
                  onClick={() => setShowNotesDrawer(false)}
                  aria-label="Close notes drawer"
                >
                  ×
                </button>
              </div>
              {loadingNotes ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      border: "2px solid #e0e7ff",
                      borderTop: "2px solid #36c0c4",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                </div>
              ) : notes.length > 0 ? (
                <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                  {notes.map((note, idx) => (
                    <li
                      key={idx}
                      style={{
                        background: "linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)",
                        color: "#334155",
                        borderRadius: "14px",
                        padding: "14px 18px",
                        marginBottom: "14px",
                        border: "1px solid #e0e7ff",
                        position: "relative",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                          <span style={{ fontSize: "10px", fontWeight: 500, color: "#666" }}>{note.author}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "10px", fontWeight: 500, color: "#666" }}>{new Date(note.date).toLocaleDateString()}</span>
                            <span style={{ display: "flex", gap: "2px" }}>
                              <button
                                className="btn btn-secondary no-rotate"
                                style={{
                                  padding: "2px",
                                  borderRadius: "50%",
                                  width: "24px",
                                  height: "24px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "none",
                                  background: "transparent",
                                  color: "#ef4444",
                                  cursor: "pointer",
                                }}
                                title="Delete"
                                onClick={() => setDeleteIdx(idx)}
                              >
                                {/* Bin SVG icon in red */}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <polyline points="3 6 5 6 21 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path
                                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                                    stroke="#ef4444"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path d="M10 11v6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M14 11v6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <rect
                                    x="9"
                                    y="3"
                                    width="6"
                                    height="3"
                                    rx="1"
                                    stroke="#ef4444"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </span>
                          </div>
                        </div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>{note.text}</div>
                      </div>
                      {deleteIdx === idx && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            width: "100%",
                            background: "#fee2e2",
                            color: "#b91c1c",
                            borderRadius: "8px",
                            padding: "10px 16px",
                            marginTop: "8px",
                            fontWeight: 600,
                            fontSize: "1em",
                            textAlign: "center",
                            zIndex: 10,
                            boxShadow: "0 2px 8px rgba(239,68,68,0.08)",
                          }}
                        >
                          Are you sure you want to delete?
                          <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "10px" }}>
                            <button
                              className="btn btn-secondary"
                              onClick={async () => {
                                if (deleteIdx !== null) {
                                  try {
                                    await archiveNote(notes[deleteIdx].id);
                                    setNotes(notes.filter((_, i) => i !== deleteIdx));
                                    setDeleteIdx(null);
                                  } catch (error) {
                                    toast.current?.show({
                                      severity: "error",
                                      summary: "Error",
                                      detail: "Failed to delete note.",
                                      life: 3000,
                                    });
                                  }
                                }
                              }}
                            >
                              Delete
                            </button>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: "7px 18px", borderRadius: "6px", fontWeight: 500, fontSize: "0.95em" }}
                              onClick={() => setDeleteIdx(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: "center", color: "#64748b", fontSize: "1em", padding: "20px" }}>No notes added</div>
              )}
            </div>
          )}
          {showConfirmationModal && (
            <div className="notes-modal-overlay modal-center">
              <div className="notes-modal">
                <h2>Confirm Ancillary Selection</h2>
                <button
                  className="modal-close-btn"
                  onClick={() => {
                    setShowConfirmationModal(false);
                    setPendingWayTemplates([]);
                    setPendingSelectedQuoteId(null);
                  }}
                  aria-label="Close confirmation modal"
                >
                  ×
                </button>
                <p>Are you sure you want to select these ancillaries?</p>
                <div className="modal-actions">
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      setShowConfirmationModal(false);
                      if (onNext) {
                        const selectedQuoteObj = quotes.find((q) => q.id === selectedQuotes);
                        const data = { selectedQuotes, globalAncillaries, vehicleCountFromApi, agencyFee, tranzpayServiceFee, carrierName: selectedQuoteObj?.carrier || "", numberOfPayments: selectedQuoteObj?.payments ?? 0 };
                        onNext(data);
                        setProceedBtnLabel("Proceed to Payment");
                      }
                      setPendingWayTemplates([]);
                      setPendingSelectedQuoteId(null);
                      setProceedBtnLabel("Proceed to Payment");
                    }}
                  >
                    Yes
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowConfirmationModal(false);
                      setPendingWayTemplates([]);
                      setPendingSelectedQuoteId(null);
                    }}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ...existing code... */}
          <div className="form-actions">
            <div className="flex w-full items-center">
              {onPrevious && (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    onPrevious();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
                  </svg>
                  Previous
                </button>
              )}
              {/* <button className="btn btn btn-primary" onClick={() => navigate("/search-client")}> 
                <span>New Client</span>
              </button> */}
              <button
                className="btn btn btn-primary ml-auto"
                disabled={proceedBtnLabel == "Processing..."}
                onClick={async () => {
                  const selectedQuoteId = selectedQuotes;

                  // Validation: Check if ancillary card is selected when in ancillary flow
                  if (isAncillaryFlow && !selectedQuoteId) {
                    toast.current?.show({
                      severity: "warn",
                      summary: "Selection Required",
                      detail: "Please select policy before proceeding.",
                      life: 3000,
                    });
                    return;
                  }
                  // Validation: In full quote flow, require a quote to be selected
                  if (!isAncillaryFlow && !selectedQuotes) {
                    toast.current?.show({
                      severity: "warn",
                      summary: "Selection Required",
                      detail: "Please select a quote before continuing.",
                      life: 3000,
                    });
                    return;
                  }

                  try {
                    // Save the selected quote ID to history (only for ancillary flow)
                    // if (isAncillaryFlow && selectedQuoteId) {
                    //   await saveQuoteHistory(selectedQuoteId);
                    // }

                    const selectedWayTemplates: string[] = [];
                    if (globalAncillaries.wayRTG3Mon) selectedWayTemplates.push(wayMappings.wayRTG3Mon);
                    if (globalAncillaries.wayRTG6Mon) selectedWayTemplates.push(wayMappings.wayRTG6Mon);
                    if (globalAncillaries.wayRoadside3Mon) selectedWayTemplates.push(wayMappings.wayRoadside3Mon);
                    if (globalAncillaries.wayRoadside6Mon) selectedWayTemplates.push(wayMappings.wayRoadside6Mon);
                    if (selectedWayTemplates.length > 0) {
                      setPendingWayTemplates(selectedWayTemplates);
                      setPendingSelectedQuoteId(selectedQuoteId);
                      setShowConfirmationModal(true);
                    } else {
                      if (onNext) {
                        let vehicleCount = isAncillaryFlow ? vehicleCountFromApi : getVehicleCount();
                        let data;
                        const selectedQuoteObj = quotes.find((q) => q.id === selectedQuotes);
                        const selectedCarrierName = selectedQuoteObj?.carrier || "";
                        if (isAncillaryFlow && selectedQuoteId) {
                          // Send SourceQuoteId as quoteId to parent
                          data = {
                            selectedData: {
                              selectedQuotes: selectedQuotes,
                              globalAncillaries: globalAncillaries,
                              ...(typeof vehicleCount === "number" ? { vehicleCount } : {}),
                              SourceQuoteId: selectedQuoteId,
                              quoteId: selectedQuoteId,
                            },
                            vehicleCount,
                            SourceQuoteId: selectedQuoteId,
                            quoteId: selectedQuoteId,
                            vehicleCountFromApi,
                            agencyFee,
                            tranzpayServiceFee,
                            carrierName: selectedCarrierName,
                            numberOfPayments: selectedQuoteObj?.payments ?? 0,
                          };
                        } else {
                          data = {
                            selectedData: {
                              selectedQuotes: selectedQuotes,
                              globalAncillaries: globalAncillaries,
                              ...(typeof vehicleCount === "number" ? { vehicleCount } : {}),
                            },
                            vehicleCount,
                            vehicleCountFromApi,
                            agencyFee,
                            tranzpayServiceFee,
                            carrierName: selectedCarrierName,
                            numberOfPayments: selectedQuoteObj?.payments ?? 0,
                          };
                        }
                        onNext(data);
                      }
                    }
                  } catch (error: any) {
                    toast.current?.show({
                      severity: "error",
                      summary: "Error",
                      detail: error.response?.data?.message || "An error occurred during proceed.",
                      life: 3000,
                    });
                  }
                }}
              >
                {proceedBtnLabel}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Return Content without Layout if being used in a stepper workflow
  if (onNext || currentStep !== undefined) {
    return <Content />;
  }

  // Return Content wrapped in Layout for standalone page
  return (
    <Layout currentPage="Compare Quotes">
      <Content />
    </Layout>
  );
}
